// @flow

/* eslint react/forbid-prop-types:0 */

import React, { Component } from 'react';
import cx from 'classnames';
import DebugInfo from 'app/common/react/components/common/DebugInfo/DebugInfo';
import DeepLink from 'app/common/react/components/growth/unauth/lib/DeepLink';
import PinImage from 'app/common/react/components/growth/unauth/pin/PinImage/PinImage';
import PinMeta from 'app/common/react/components/growth/unauth/pin/PinMeta/PinMeta';
import PropTypes from 'prop-types';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type ExperimentsClient from 'app/common/lib/ExperimentsClient';
import type { ContextType } from 'app/common/react/components/growth/unauth/lib/PropTypes';
import UnauthUsage from 'app/common/lib/UnauthUsage';
import { buildPinDeeplinks } from 'app/common/lib/deeplinkRoutes';
import { compose } from 'redux';
import { getAttributionTitleText } from 'app/packages/pin-utils/unauthPinUtils';
import withUnauthSeoExperiments, {
  ExperimentsPropType,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  calculatePinMetaHeight,
  calculatePinImageHeight,
  calculateRelatedKlpStoryHeight,
  calculateDescriptionNumLines,
  shouldRenderAttributionTitle,
  getDescriptionAndAnnotations,
} from 'app/common/react/components/growth/unauth/pin/pinUtils';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { type PinType, pinPropType } from 'app/common/react/components/growth/unauth/lib/PropTypes'; // Needs work

type State = {
  toggled: boolean,
};

type Props = {
  addImageParam?: boolean,
  clientJsActive: boolean,
  carouselTagColor: ?string,
  desktopUnauthRankingLoggingData: ?{
    id: string,
  },
  disableDeeplinks: boolean,
  fromRelatedPinSection?: boolean,
  isMainPin?: boolean,
  isPaidTraffic: boolean,
  isRelatedPin: boolean,
  maxPinImageHeight: number,
  pin: PinType,
  pinImageClickHandler: ?Function,
  pinImageSize: string,
  pinImageWidthMax: number,
  pinImageWidthMin: number,
  pinLinkClickHandler: ?Function,
  pinNumber: number,
  pinWrapperBackgroundColor?: string,
  handlePinInGridClick: ?Function,
  hideMeta: boolean,
  hideAllMeta: boolean,
  hideTitle?: boolean,
  hideVaseTags?: boolean,
  requestContext: RequestContextType,
  secondaryFeed?: boolean,
  showRichPinAttribution?: boolean,
  seoUnauthExperiments: ExperimentsClient,
  showSeeMore: boolean,
  slotIndex: number,
  srcSet: Object,
  numServerRenderedPins: number,
  vaseClickHandler?: (PinType) => void,
  vaseCarouselStyleOverrides: {
    gradientLeft?: {
      background: string,
    },
    gradientRight?: {
      background: string,
    },
  },
  openInCurrentTab: boolean,
  viewParameter: ?number,
};

export const calculatePinHeight = (
  pin: PinType,
  maxImageHeight: number,
  pageType: string,
  seoUnauthExperiments: ExperimentsClient,
  hideMeta: boolean,
  hideAllMeta: ?boolean,
  isPaidTraffic: boolean,
  locale: string,
) => {
  if (pin.type === 'relatedKlpStory') {
    return calculateRelatedKlpStoryHeight(pin.data.length);
  } else if (pin.type === 'placeholder') {
    return pin.height;
  } else {
    const pinImageHeight = calculatePinImageHeight(pin, maxImageHeight);
    const pinMetaHeight = hideAllMeta
      ? 0
      : calculatePinMetaHeight(
          pin,
          locale,
          seoUnauthExperiments,
          hideMeta,
          isPaidTraffic,
          pageType,
        );
    return pinImageHeight + pinMetaHeight;
  }
};

class Pin extends Component<Props, State> {
  state = {
    toggled: false,
  };

  context: ContextType;

  trackScopedAction = (action) => {
    trackRegisterAction(`unauth_pin_rep.${action}.${String(this.props.viewParameter)}`);
  };

  handleClick = (e: SyntheticMouseEvent<>) => {
    if (this.props.handlePinInGridClick) {
      this.props.handlePinInGridClick();
    }
    UnauthUsage.logUnauthHybridCloseup();
    this.trackScopedAction('click');

    const isPressingCmdOrWinKey = e.metaKey;

    if (this.props.pinImageClickHandler && !isPressingCmdOrWinKey) {
      this.props.pinImageClickHandler(e, this.props.pin, this.props.fromRelatedPinSection);
    }

    const deepLink = new DeepLink(this.context.userAgent.raw, this.context.userAgent.platform);
    if (
      !this.props.disableDeeplinks &&
      deepLink.shouldDeepLink(
        document.referrer,
        this.context.userAgent,
        // $FlowIssue: contextTypes never asked for this, but this is unauth
        this.context.isAuthenticated,
      )
    ) {
      e.preventDefault();
      deepLink.navigateToDeepLink(this.context.unauthId, buildPinDeeplinks(this.props.pin.id));
    } else if (!this.props.pinImageClickHandler || isPressingCmdOrWinKey) {
      if (isPressingCmdOrWinKey) {
        this.trackScopedAction('click.cmd_button_pressed');
      }

      e.preventDefault();
      // If no pin image click handler is given, open link in new tab
      const target = this.props.openInCurrentTab && !isPressingCmdOrWinKey ? '_self' : '_blank';
      const path = '/pin/' + this.props.pin.id + '/';
      const win = window.open(path, target);
      win.focus();
    }
  };

  handleClickSeeMore = (toggle) => {
    this.setState({ toggled: toggle });
  };

  render() {
    const {
      isRelatedPin,
      hideMeta,
      hideTitle,
      pin,
      vaseClickHandler,
      pinNumber,
      clientJsActive,
      pinWrapperBackgroundColor,
      seoUnauthExperiments,
      vaseCarouselStyleOverrides,
      carouselTagColor,
      secondaryFeed,
      hideAllMeta = false,
      hideVaseTags = false,
      showRichPinAttribution = false,
      addImageParam = false,
      isMainPin = false,
      requestContext,
    } = this.props;

    let pinWrapperStyle = { width: this.props.pinImageWidthMax };
    const pinWrapperClassName = ['PinGridInner__brioPin'];
    const inSimilarPinsGroup =
      (seoUnauthExperiments.v2GetGroup('remove_similar_pins_plp') || '').startsWith('enabled') ||
      (
        ((requestContext.locale === 'pt-PT' ||
          requestContext.locale === 'pt-BR' ||
          requestContext.locale === 'fr') &&
          seoUnauthExperiments.v2GetGroup('remove_similar_pins_plp_rollout')) ||
        ''
      ).startsWith('enabled');

    let carouselTagColorFinal = carouselTagColor;
    if (inSimilarPinsGroup) {
      carouselTagColorFinal = 'rgb(235, 235, 232)';
    }

    if (this.state.toggled) {
      pinWrapperClassName.push('GrowthUnauthPin_brioPin_toggled');
      carouselTagColorFinal = '#efefef';
    } else {
      pinWrapperClassName.push('GrowthUnauthPin_brioPin');
      if (pinWrapperBackgroundColor) {
        pinWrapperStyle = { ...pinWrapperStyle, background: pinWrapperBackgroundColor };
      }
      if (!clientJsActive) {
        pinWrapperStyle = { ...pinWrapperStyle, position: 'absolute' };
        pinWrapperClassName.push('Pin__' + pinNumber);
      }
      if (isRelatedPin) {
        pinWrapperClassName.push('relatedPin');
      }
    }

    let pinGridOptGroup = '';
    if (this.context.pageType === 'pin') {
      pinGridOptGroup =
        seoUnauthExperiments.v2GetGroup('pin_grid_style_format_optimization_pin_v3') || '';
    } else if (this.context.pageType === 'board') {
      pinGridOptGroup =
        seoUnauthExperiments.v2GetGroup('pin_grid_style_format_optimization_board_v3') || '';
    }

    const inPinGridOptGroup = pinGridOptGroup.startsWith('enabled');

    let pinTitle = null;
    const title = getAttributionTitleText(pin) || '';

    const { description } = getDescriptionAndAnnotations(
      pin,
      seoUnauthExperiments,
      this.context.pageType,
    );

    const showTitle = shouldRenderAttributionTitle(pin) ? title : description;
    const numLinesForTitle = calculateDescriptionNumLines(showTitle, seoUnauthExperiments);

    const pinTitleContents = showTitle ? (
      <h3
        className={'PinDescription__desc PinDescription__' + numLinesForTitle + 'LineDesc'}
        data-test-id="desc"
      >
        {showTitle}
      </h3>
    ) : null;

    if (pinGridOptGroup.startsWith('enabled_structure')) {
      pinTitle = pinTitleContents;
    } else if (inPinGridOptGroup) {
      pinTitle = <figcaption>{pinTitleContents}</figcaption>;
    }

    return (
      /* eslint-disable jsx-a11y/click-events-have-key-events */
      <div
        className={cx(pinWrapperClassName)}
        data-test-id="pin"
        data-test-pin-id={pin.id}
        onClick={(e) => {
          trackRegisterAction('unauth.pin_image_hover_image_click');
          if (!this.state.toggled) {
            this.handleClick(e);
          }
        }}
        onContextMenu={(e) => {
          e.stopPropagation();
          trackRegisterAction('unauth.context_menu_open.pin_image_hover_image');
        }}
        role="button"
        style={pinWrapperStyle}
        tabIndex="0"
      >
        {/* eslint-enable jsx-a11y/click-events-have-key-events */}
        {/* $FlowFixMe: new errors found from flow 0.96 upgrade */}
        <PinImage
          clientJsActive={this.props.clientJsActive}
          desktopUnauthRankingLoggingData={this.props.desktopUnauthRankingLoggingData}
          imageSize={this.props.pinImageSize}
          numServerRenderedPins={this.props.numServerRenderedPins}
          maxPinImageHeight={this.props.maxPinImageHeight}
          hideTitle={hideTitle}
          pin={pin}
          pinImageWidthMax={this.props.pinImageWidthMax}
          pinImageWidthMin={this.props.pinImageWidthMin}
          pinLinkClickHandler={this.props.pinLinkClickHandler}
          slotIndex={this.props.slotIndex}
          srcSet={this.props.srcSet}
          imageClickHandler={this.handleClick}
          viewParameter={this.props.viewParameter}
          pinTitle={pinTitle}
          addImageParam={addImageParam}
          isMainPin={isMainPin}
        />
        {hideAllMeta ? null : (
          <PinMeta
            carouselTagColor={carouselTagColorFinal}
            disableDeeplinks={this.props.disableDeeplinks}
            desktopUnauthRankingLoggingData={this.props.desktopUnauthRankingLoggingData}
            hideMeta={hideMeta}
            hideVaseTags={hideVaseTags}
            isPaidTraffic={this.props.isPaidTraffic}
            openInCurrentTab={this.props.openInCurrentTab}
            pin={pin}
            inPinGridOptGroup={inPinGridOptGroup}
            seoUnauthExperiments={seoUnauthExperiments}
            showSeeMore={this.props.showSeeMore}
            vaseClickHandler={vaseClickHandler}
            vaseCarouselStyleOverrides={vaseCarouselStyleOverrides}
            secondaryFeed={secondaryFeed}
            showRichPinAttribution={showRichPinAttribution}
            handleClickSeeMore={this.handleClickSeeMore}
            pinGridOptGroup={pinGridOptGroup}
          />
        )}
        {pin.debug && Object.keys(pin.debug).length !== 0 ? <DebugInfo data={pin.debug} /> : null}
      </div>
    );
  }
}

Pin.propTypes = {
  clientJsActive: PropTypes.bool,
  carouselTagColor: PropTypes.string,
  desktopUnauthRankingLoggingData: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  disableDeeplinks: PropTypes.bool,
  isPaidTraffic: PropTypes.bool,
  isRelatedPin: PropTypes.bool,
  maxPinImageHeight: PropTypes.number,
  handlePinInGridClick: PropTypes.func,
  pin: pinPropType,
  pinImageClickHandler: PropTypes.func,
  pinImageSize: PropTypes.string.isRequired,
  pinImageWidthMax: PropTypes.number.isRequired,
  pinImageWidthMin: PropTypes.number.isRequired,
  pinLinkClickHandler: PropTypes.func,
  pinNumber: PropTypes.number.isRequired,
  pinWrapperBackgroundColor: PropTypes.string,
  hideMeta: PropTypes.bool,
  hideAllMeta: PropTypes.bool,
  secondaryFeed: PropTypes.bool,
  seoUnauthExperiments: ExperimentsPropType,
  showSeeMore: PropTypes.bool,
  slotIndex: PropTypes.number,
  srcSet: PropTypes.object,
  numServerRenderedPins: PropTypes.number,
  vaseClickHandler: PropTypes.func,
  openInCurrentTab: PropTypes.bool,
  showRichPinAttribution: PropTypes.bool,
  viewParameter: PropTypes.number,
  vaseCarouselStyleOverrides: PropTypes.shape({}),
};

Pin.contextTypes = {
  pageType: PropTypes.string.isRequired,
  unauthId: PropTypes.string.isRequired,
  userAgent: PropTypes.shape({
    canUseNativeApp: PropTypes.bool.isRequired,
    isTablet: PropTypes.bool.isRequired,
  }).isRequired,
};

export default compose(
  withRequestContext,
  withUnauthSeoExperiments,
)(Pin);
